import { mat4 } from 'gl-matrix';
import { Context } from '../../context/Context';
import { Camera } from '../../camera';
import { Scene } from '../../scene/Scene';
import {
  DirectionalLight,
  Light,
  PointLight,
  SpotLight,
} from '../../light';
import { TileLightAssigner } from '../../light';
import { LightDataTexture } from '../../texture/LightDataTexture';
import { TileDataTexture } from '../../texture/TileDataTexture';
import { DeferredLightShaderSettings } from '../../shader/generated';
import { toRadian } from '../../math';
import { GBuffer } from '../GBuffer';
import { FullscreenQuad } from '../FullscreenQuad';
import { SSAOPass } from '../SSAOPass';
/**
 * Light data shared with the forward transparency pass so it can iterate the
 * same per-tile light lists without recomputing them.
 */
export type SharedLightData = {
  lightDataTexture: LightDataTexture;
  tileDataTexture: TileDataTexture;
  tilesX: number;
  tileSize: number;
};

export type LightingStats = {
  lightingMs: number;
};

/**
 * Deferred lighting: ambient pass (also writes gl_FragDepth for transparency),
 * batched fullscreen pass for shadowless lights via tile lists, and one
 * fullscreen pass per shadow-casting light.
 */
export class LightingPass {
  readonly lightDataTexture: LightDataTexture;
  readonly tileDataTexture: TileDataTexture;
  readonly tileLightAssigner = new TileLightAssigner();
  // CPU time of the last TileLightAssigner.assign() call. Read after render()
  // by the deferred renderer to surface this carved-out cost in the perf HUD.
  tileAssignMs = 0;

  private readonly scratchShadowMatrix = mat4.create();
  private readonly shadowLightsCache: Light[] = [];
  private readonly shadowSetCache = new Set<Light>();

  constructor(ctx: Context) {
    this.lightDataTexture = new LightDataTexture(ctx.gl);
    this.tileDataTexture = new TileDataTexture(ctx.gl);
  }

  destroy(): void {
    this.lightDataTexture.destroy();
    this.tileDataTexture.destroy();
  }

  /**
   * Run the full deferred lighting pipeline against the bound output FBO.
   * Returns shared light data so the transparency pass can reuse it.
   */
  render(
    ctx: Context,
    scene: Scene,
    camera: Camera,
    lights: Light[],
    gBuffer: GBuffer,
    quad: FullscreenQuad,
    ssaoPass: SSAOPass,
    ssaoEnabled: boolean,
  ): SharedLightData {
    const gl = ctx.gl;

    // 1. Ambient pass — writes gl_FragDepth for the forward transparency pass.
    // Use depthFunc(ALWAYS) so the fullscreen quad always passes depth test
    // while still writing the reconstructed depth values.
    gl.enable(gl.DEPTH_TEST);
    gl.depthFunc(gl.ALWAYS);
    gl.depthMask(true);
    this.renderAmbientPass(ctx, scene, camera, gBuffer, quad, ssaoPass, ssaoEnabled);
    gl.depthFunc(gl.GREATER);
    gl.depthMask(false);

    // 2. Per-light passes with additive blending (no depth test or write)
    gl.disable(gl.DEPTH_TEST);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE);

    // Partition: shadow-casting lights render individually, shadowless batch together
    const shadowLights = this.shadowLightsCache;
    shadowLights.length = 0;
    const shadowSet = this.shadowSetCache;
    shadowSet.clear();
    for (const light of lights) {
      if (light instanceof DirectionalLight || light.shadowMap) {
        shadowLights.push(light);
        shadowSet.add(light);
      }
    }

    // Upload ALL lights once with shadow flag in d2.w. The batched deferred
    // shader skips flagged lights; the forward transparent shader processes all.
    // Tile assignment also computed once and reused for both passes.
    this.tileAssignMs = 0;
    if (lights.length > 0) {
      this.lightDataTexture.update(lights, shadowSet);
      const tileAssignStart = performance.now();
      this.tileLightAssigner.assign(camera, lights, ctx.viewPort);
      this.tileAssignMs = performance.now() - tileAssignStart;
      this.tileDataTexture.update(this.tileLightAssigner.tileData, this.tileLightAssigner.numTiles);
    }

    // Batched pass: one fullscreen draw — shader skips shadow-flagged lights
    if (lights.length > shadowLights.length) {
      this.renderBatchedLightPass(ctx, camera, gBuffer, quad);
    }

    // Individual passes: shadow-casting lights only
    for (const light of shadowLights) {
      this.renderLightPass(ctx, light, camera, gBuffer, quad);
    }

    gl.disable(gl.BLEND);

    return {
      lightDataTexture: this.lightDataTexture,
      tileDataTexture: this.tileDataTexture,
      tilesX: this.tileLightAssigner.tilesX,
      tileSize: this.tileLightAssigner.tileSize,
    };
  }

  private renderAmbientPass(
    ctx: Context,
    scene: Scene,
    camera: Camera,
    gBuffer: GBuffer,
    quad: FullscreenQuad,
    ssaoPass: SSAOPass,
    ssaoEnabled: boolean,
  ): void {
    const gl = ctx.gl;
    const shader = ctx.engine.shaderManager.getDeferredLightShader('AMBIENT_PASS');
    ctx.setShader(shader);

    shader.uniformsContainer
      .setAlbedoMap(gBuffer.albedoTexture)
      .setNormalMap(gBuffer.normalTexture)
      .setDepthMap(gBuffer.depthTexture)
      .setInvViewProjection(camera.invViewProjection)
      .setCameraPosition(camera.cameraPosition)
      .setAmbientColor(scene.lightManager.ambientColor)
      .setSsaoMap(ssaoEnabled ? ssaoPass.ssaoTexture : ssaoPass.whiteTexture);

    shader.validate();
    quad.draw(gl);
  }

  private renderBatchedLightPass(
    ctx: Context,
    camera: Camera,
    gBuffer: GBuffer,
    quad: FullscreenQuad,
  ): void {
    const gl = ctx.gl;
    const shader = ctx.engine.shaderManager.getDeferredLightShader('BATCHED_LIGHTS');
    ctx.setShader(shader);

    shader.uniformsContainer
      .setAlbedoMap(gBuffer.albedoTexture)
      .setNormalMap(gBuffer.normalTexture)
      .setDepthMap(gBuffer.depthTexture)
      .setInvViewProjection(camera.invViewProjection)
      .setLightDataMap(this.lightDataTexture.texture)
      .setTileDataMap(this.tileDataTexture.texture)
      .setTilesX(this.tileLightAssigner.tilesX)
      .setTileSize(this.tileLightAssigner.tileSize)
      .setLightCount(this.lightDataTexture.count)
      .setLightDataHeight(this.lightDataTexture.capacity)
      .setCameraPosition(camera.cameraPosition);

    shader.validate();
    quad.draw(gl);
  }

  private renderLightPass(
    ctx: Context,
    light: Light,
    camera: Camera,
    gBuffer: GBuffer,
    quad: FullscreenQuad,
  ): void {
    const gl = ctx.gl;

    // Choose shader variant based on light type and shadow
    const settings: DeferredLightShaderSettings = [];

    if (light instanceof DirectionalLight) {
      settings.push('DIRECTIONAL');
      if (light.cascadedShadowMap) settings.push('SHADOW_CSM');
    } else if (light.shadowMap) {
      settings.push(light.shadowMap.isCube ? 'SHADOW_CUBE' : 'SHADOW_2D');
    }

    const shader = ctx.engine.shaderManager.getDeferredLightShader(...settings);
    ctx.setShader(shader);

    // Shared uniforms
    shader.uniformsContainer
      .setAlbedoMap(gBuffer.albedoTexture)
      .setNormalMap(gBuffer.normalTexture)
      .setDepthMap(gBuffer.depthTexture)
      .setInvViewProjection(camera.invViewProjection)
      .setLightPosition(light.position as number[])
      .setLightColor(light.color as number[])
      .setLightType(light.type)
      .setCameraPosition(camera.cameraPosition)
      .setTaaFrameIndex(camera.taaFrameIndex);

    // Type-specific uniforms and shadow binding
    if (light instanceof DirectionalLight) {
      shader.uniformsContainer
        .setLightDirection(light.direction as number[])
        .setLightRanges([0, 0, -1, -1]);

      if (light.cascadedShadowMap) {
        const csm = light.cascadedShadowMap;
        shader.uniformsContainer
          .setShadowCascadeMap0(csm.momentsTextures[0])
          .setShadowCascadeMap1(csm.momentsTextures[1])
          .setShadowCascadeMap2(csm.momentsTextures[2])
          .setShadowCascadeMap3(csm.momentsTextures[3])
          .setCascadeViewMatrix(0, csm.cascadeViewMatrices[0])
          .setCascadeViewMatrix(1, csm.cascadeViewMatrices[1])
          .setCascadeViewMatrix(2, csm.cascadeViewMatrices[2])
          .setCascadeViewMatrix(3, csm.cascadeViewMatrices[3])
          .setCascadeSplits(csm.cascadeSplits)
          .setViewMatrix(camera.modelViewMatrix);
      }
    } else if (light instanceof SpotLight) {
      shader.uniformsContainer
        .setLightDirection(light.direction as number[])
        .setLightRanges([
          light.innerRange,
          light.outerRange,
          Math.cos(toRadian(light.dotInnerLimit)),
          Math.cos(toRadian(light.dotOuterLimit)),
        ]);

      if (light.shadowMap) {
        mat4.mul(this.scratchShadowMatrix, light.camera.projectionMatrix, light.camera.modelViewMatrix);
        shader.uniformsContainer.setShadowMap(light.shadowMap).setShadowViewMatrix(this.scratchShadowMatrix);
      }
    } else if (light instanceof PointLight) {
      shader.uniformsContainer
        .setLightDirection([0, 0, 0])
        .setLightRanges([light.innerRange, light.outerRange, -1, -1]);

      if (light.shadowMap) {
        shader.uniformsContainer
          .setShadowMapCube(light.shadowMap as any)
          .setShadowLightRange(light.outerRange);
      }
    }

    shader.validate();
    quad.draw(gl);
  }
}
